import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomShowModal from '../../components/Common/CustomShowModal';
import * as actions from './actions';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const ServiceCard = styled(Box)({
  background: '#FFFFFF',
  borderRadius: 14,
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  transition: 'all 0.25s ease',
  cursor: 'pointer',
  textDecoration: 'none',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  border: '1px solid transparent',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: '1px solid rgba(26,115,232,0.2)',
    '& .open-icon': {
      opacity: 1,
    },
  },
});

const serviceColors = {
  spotify: '#1DB954',
  appleMusic: '#FC3C44',
  youtubeMusic: '#FF0000',
  youtube: '#FF0000',
  amazonMusic: '#00A8E1',
  deezer: '#A238FF',
  tidal: '#000000',
  soundcloud: '#FF5500',
  pandora: '#224099',
  iHeartRadio: '#C6002B',
  bandcamp: '#629AA9',
  napster: '#007AFF',
  jioSaavn: '#2BC5B4',
  gaana: '#E72C30',
  tiktokMusic: '#FE2C55',
  instagram: '#E1306C',
  qqMusic: '#31C27C',
  netease: '#C20C0C',
  yandex: '#FFCC00',
  audius: '#CC0FE0',
  lineMusic: '#00B900',
  awa: '#FC6631',
  kkbox: '#09CEF6',
  wynkMusic: '#1D9CEF',
  qobuz: '#296FA8',
  sevenDigital: '#E22A2A',
  amazon: '#FF9900',
  shazam: '#0088FF',
  youtubeShorts: '#FF0000',
};

function formatServiceName(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeServiceName(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const serviceKeyToLogoName = {
  amazonMusic: 'amazon',
  tiktokMusic: 'tiktok',
  instagram: 'facebook,instagram',
  netease: 'neteasecloudmusic',
  sevenDigital: '7digital',
  youtube: 'youtubemusic',
  youtubeShorts: 'youtubemusic',
};

function getLogoUrl(serviceKey, logosArray) {
  const targetName = serviceKeyToLogoName[serviceKey] || serviceKey;
  const normalized = normalizeServiceName(targetName);
  const match = logosArray.find(
    (item) => normalizeServiceName(item.name) === normalized
  );
  return match ? match.logo : null;
}

function formatDuration(ms) {
  if (!ms) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function buildLinksText(data, audio) {
  let text = `Title: ${data.track_name || audio?.title || 'Unknown Track'}\n\n`;
  const services = data.services || {};
  for (const [name, service] of Object.entries(services)) {
    if (service && service.link) {
      text += `${formatServiceName(name)}: ${service.link}\n`;
    }
  }
  text += `\nGenerated by Fronic Media\nhttps://fronicmedia.com/`;
  return text;
}

function downloadAsTextFile(text, songName) {
  const sanitized = (songName || 'links').replace(/[<>:"/\\|?*]/g, '_').trim() || 'links';
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${sanitized}.txt`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ViewLinksModal({ open, handleClose, audio, logos = [] }) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copiedLink, setCopiedLink] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (open && audio) {
      loadCachedLinks();
    }
    if (!open) {
      setData(null);
      setError('');
      setLoading(false);
      setFetching(false);
      setCopiedLink('');
      setCopiedAll(false);
    }
  }, [open, audio]);

  const loadCachedLinks = () => {
    setLoading(true);
    setError('');
    actions.getMusicLinks(audio.audio_id)
      .then((res) => {
        setLoading(false);
        setData(res.body);
      })
      .catch((err) => {
        setLoading(false);
        setError(err.message || 'Error fetching links. Contact team via support create ticket');
      });
  };

  const handleFetch = () => {
    setFetching(true);
    setError('');
    actions.fetchMusicLinks(audio.audio_id)
      .then((res) => {
        setFetching(false);
        setData(res.body);
      })
      .catch((err) => {
        setFetching(false);
        // If fetch limit reached, update fetch_count on existing data but don't wipe the view
        if (err.body && err.body.fetch_count !== undefined && data) {
          setData({ ...data, fetch_count: err.body.fetch_count });
        } else if (!data) {
          setError(err.message || 'Error fetching links. Contact team via support create ticket');
        }
      });
  };

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={36} sx={{ color: '#1a73e8' }} />
          <Typography sx={{ mt: 2, color: '#5f6368', fontSize: '0.875rem' }}>
            Checking for links...
          </Typography>
        </Box>
      );
    }

    // Error state
    if (error) {
      return (
        <Box sx={{ py: 4, px: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
        </Box>
      );
    }

    if (!data) return null;

    // No ISRC
    if (data.has_isrc === false) {
      return (
        <Box sx={{ py: 4, px: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            Error fetching links. Contact team via support create ticket
          </Alert>
        </Box>
      );
    }

    // No cached data, ISRC available
    if (!data.cached) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          {fetching ? (
            <>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'pulse 1.8s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.08)', opacity: 0.7 },
                },
              }}>
                <MusicNoteIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1a73e8' }}>
                Fetching streaming links...
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#9e9e9e', mt: 0.5 }}>
                This may take a few seconds
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
                background: 'linear-gradient(135deg, rgba(26,115,232,0.08) 0%, rgba(0,191,165,0.08) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <LinkIcon sx={{ color: '#1a73e8', fontSize: 32 }} />
              </Box>
              <Typography sx={{ mb: 1, color: '#202124', fontSize: '1.0625rem', fontWeight: 700 }}>
                No streaming links found
              </Typography>
              <Typography sx={{ mb: 3, color: '#5f6368', fontSize: '0.875rem' }}>
                Click below to search for this track across streaming platforms
              </Typography>
              <Button
                variant="contained"
                onClick={handleFetch}
                startIcon={<LinkIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4, py: 1.5,
                  fontSize: '0.9375rem',
                  boxShadow: '0 4px 14px rgba(26,115,232,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                    boxShadow: '0 6px 20px rgba(26,115,232,0.4)',
                  },
                }}
              >
                Fetch Links
              </Button>
              <Typography sx={{ mt: 2, color: '#202124', fontSize: '0.75rem' }}>
                {5 - (data.fetch_count || 0)}/5 fetches left
              </Typography>
            </>
          )}
        </Box>
      );
    }

    // Cached data — main view
    const services = data.services || {};
    const serviceEntries = Object.entries(services).filter(
      ([, service]) => service && service.link
    );
    const fetchCount = data.fetch_count || 0;
    const limitReached = fetchCount >= 5;
    const duration = formatDuration(data.track_duration);

    return (
      <Box>
        {/* Blinking loader while refetching */}
        {fetching ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pulse 1.8s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.08)', opacity: 0.7 },
              },
            }}>
              <MusicNoteIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1a73e8' }}>
              Refetching streaming links...
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#9e9e9e', mt: 0.5 }}>
              This may take a few seconds
            </Typography>
          </Box>
        ) : (
          <>
            {/* Hero: Track image + info side by side */}
            <Box sx={{
              display: 'flex', gap: 3, mb: 3, p: 2.5,
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)',
              borderRadius: 4,
              border: '1px solid rgba(26,115,232,0.08)',
            }}>
              {/* Track artwork */}
              {data.track_image ? (
                <Box
                  component="img"
                  src={data.track_image}
                  alt={data.track_name}
                  referrerPolicy="no-referrer"
                  sx={{
                    width: 120, height: 120,
                    borderRadius: 3,
                    objectFit: 'cover',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box sx={{
                  width: 120, height: 120, borderRadius: 3, flexShrink: 0,
                  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(26,115,232,0.25)',
                }}>
                  <MusicNoteIcon sx={{ color: '#fff', fontSize: 48 }} />
                </Box>
              )}

              {/* Track info */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 800, fontSize: '1.25rem', color: '#202124',
                  lineHeight: 1.3, mb: 0.5,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {data.track_name || audio?.title || 'Unknown Track'}
                </Typography>

                {audio?.primary_artist1 && (
                  <Typography sx={{ fontSize: '0.875rem', color: '#5f6368', mb: 1.5 }}>
                    {audio.primary_artist1}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{
                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                    color: '#fff', borderRadius: '20px', px: 1.5, py: 0.3,
                    fontSize: '0.75rem', fontWeight: 700,
                  }}>
                    {serviceEntries.length} platform{serviceEntries.length !== 1 ? 's' : ''}
                  </Box>
                  {duration && (
                    <Typography sx={{ fontSize: '0.8125rem', color: '#9e9e9e', fontWeight: 500 }}>
                      {duration}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.75rem', color: '#202124', ml: 'auto' }}>
                    {5 - fetchCount}/5 fetches left
                  </Typography>
                </Box>

                {/* Actions row */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'flex-end' }}>
                  <Box
                    onClick={() => {
                      const text = buildLinksText(data, audio);
                      downloadAsTextFile(text, data.track_name || audio?.title);
                    }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      border: '1px solid rgba(0, 150, 136, 0.3)',
                      borderRadius: '8px',
                      color: '#009688',
                      px: 1.2, py: 0.4,
                      fontSize: '0.7rem', fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { background: 'rgba(0, 150, 136, 0.06)', borderColor: 'rgba(0, 150, 136, 0.5)' },
                    }}
                  >
                    <FileDownloadIcon sx={{ fontSize: 14 }} /> Export
                  </Box>
                  <Box
                    onClick={() => {
                      const text = buildLinksText(data, audio);
                      navigator.clipboard.writeText(text).then(() => {
                        setCopiedAll(true);
                        setTimeout(() => setCopiedAll(false), 2000);
                      });
                    }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      border: copiedAll ? '1px solid rgba(76, 175, 80, 0.4)' : '1px solid rgba(156, 39, 176, 0.3)',
                      borderRadius: '8px',
                      color: copiedAll ? '#4caf50' : '#9C27B0',
                      px: 1.2, py: 0.4,
                      fontSize: '0.7rem', fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: copiedAll ? 'rgba(76, 175, 80, 0.06)' : 'rgba(156, 39, 176, 0.06)',
                        borderColor: copiedAll ? 'rgba(76, 175, 80, 0.5)' : 'rgba(156, 39, 176, 0.5)',
                      },
                    }}
                  >
                    {copiedAll ? <><CheckIcon sx={{ fontSize: 14 }} /> Copied!</> : <><ContentCopyIcon sx={{ fontSize: 14 }} /> Copy All</>}
                  </Box>
                  <Button
                    size="small"
                    onClick={handleFetch}
                    disabled={limitReached}
                    startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: limitReached ? '#bdbdbd' : '#1a73e8',
                      px: 1.5, py: 0.4,
                      minHeight: 0,
                      border: '1px solid',
                      borderColor: limitReached ? '#e0e0e0' : 'rgba(26,115,232,0.3)',
                      cursor: limitReached ? 'not-allowed' : 'pointer',
                      '&.Mui-disabled': {
                        pointerEvents: 'auto',
                        cursor: 'not-allowed',
                        color: '#bdbdbd',
                        borderColor: '#e0e0e0',
                      },
                      '&:hover': {
                        background: limitReached ? 'transparent' : 'rgba(26,115,232,0.06)',
                        borderColor: limitReached ? '#e0e0e0' : '#1a73e8',
                      },
                    }}
                  >
                    {limitReached ? 'Fetch limit reached' : 'Refetch Links'}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Section label */}
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>
              Available on
            </Typography>

            {/* Service cards grid */}
            {serviceEntries.length > 0 ? (
              <Grid container spacing={1.5}>
                {serviceEntries.map(([name, service]) => {
                  const color = serviceColors[name] || '#1a73e8';
                  const logoUrl = getLogoUrl(name, logos);
                  return (
                    <Grid item xs={6} sm={4} key={name}>
                      <ServiceCard>
                        {/* Color accent bar */}
                        <Box sx={{
                          position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: 3, borderRadius: '3px 0 0 3px', background: color,
                        }} />

                        {logoUrl ? (
                          <Box
                            component="img"
                            src={logoUrl}
                            alt={formatServiceName(name)}
                            sx={{ width: 72, height: 72, borderRadius: 2, objectFit: 'contain', flexShrink: 0, ml: 0.5 }}
                          />
                        ) : (
                          <Box sx={{
                            width: 72, height: 72, borderRadius: 2, flexShrink: 0, ml: 0.5,
                            backgroundColor: `${color}14`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1.4rem', color: color, fontWeight: 700,
                          }}>
                            {formatServiceName(name).charAt(0)}
                          </Box>
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#202124', fontWeight: 600,
                            fontSize: '0.8125rem', lineHeight: 1.3,
                            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {formatServiceName(name)}
                        </Typography>

                        <Tooltip title={copiedLink === name ? 'Copied!' : 'Copy link'} arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigator.clipboard.writeText(service.link);
                              setCopiedLink(name);
                              setTimeout(() => setCopiedLink(''), 2000);
                            }}
                            sx={{
                              flexShrink: 0, p: 0.5,
                              color: copiedLink === name ? '#4caf50' : '#bdbdbd',
                              '&:hover': { color: copiedLink === name ? '#4caf50' : '#1a73e8', background: 'rgba(26,115,232,0.06)' },
                            }}
                          >
                            {copiedLink === name ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open in new tab" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(service.link, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{
                              flexShrink: 0, p: 0.5,
                              color: '#bdbdbd',
                              '&:hover': { color: '#1a73e8', background: 'rgba(26,115,232,0.06)' },
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </ServiceCard>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                No streaming links available for this track.
              </Alert>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <CustomShowModal
      open={open}
      handleClose={handleClose}
      maxWidth="md"
      title={audio ? `Streaming Links - ${audio.title}` : 'Streaming Links'}
    >
      {renderContent()}
    </CustomShowModal>
  );
}
